/**
 * Stytch External Connections Creation Hook
 *
 * This hook provides functionality for creating External Connections
 * authentication using the Stytch B2B headless client.
 */
import { useCallback } from 'react';
import { getStytchClient } from '../../../../adapters/react/client/headless-client';
export function useStytchCreateExternalConnection() {
    return useCallback(async (data) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            const result = await stytch.sso.external.createConnection({
                display_name: data.displayName,
                external_organization_id: data.externalOrganizationId,
                external_connection_id: data.externalConnectionId,
            });
            return {
                success: true,
                statusCode: result.status_code,
                connection: result.connection,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to create external connection',
                data: error,
            };
        }
    }, []);
}
export function useStytchUpdateExternalConnection() {
    return useCallback(async (data) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            // CRITICAL: Only include fields that are explicitly provided to prevent
            // accidentally clearing existing SSO configuration. Stytch interprets
            // undefined fields as "clear this value" rather than "don't change".
            const updatePayload = {
                connection_id: data.connectionId,
            };
            if (data.displayName !== undefined) {
                updatePayload.display_name = data.displayName;
            }
            if (data.externalConnectionImplicitRoleAssignments !== undefined) {
                updatePayload.external_connection_implicit_role_assignments =
                    data.externalConnectionImplicitRoleAssignments;
            }
            if (data.externalGroupImplicitRoleAssignments !== undefined) {
                updatePayload.external_group_implicit_role_assignments =
                    data.externalGroupImplicitRoleAssignments;
            }
            const result = await stytch.sso.external.updateConnection(updatePayload);
            return {
                success: true,
                requestId: result.request_id,
                statusCode: result.status_code,
                connection: result.connection,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to update external connection',
                data: error,
            };
        }
    }, []);
}
